/**
 * Combat FX (plan 08 G2). Everything here is a pure function of the SimState snapshot: tracers mirror
 * sim bullets, muzzle flashes come from the closed-form last-volley time, and bursts are
 * f(sim.t − ev.t, hash) over a ring of sim-stamped events — no dt accumulation, so a paused frame is
 * frozen exactly and a replay matches. Four thin-instance draws (tracer, muzzle, additive sparks,
 * alpha smoke) share one procedural soft sprite.
 */
import { Color3, Constants, DynamicTexture, Mesh, MeshBuilder, StandardMaterial, Texture, VertexBuffer } from '@babylonjs/core/pure';
import type { Scene } from '@babylonjs/core/pure';
import { BRIDGE_HALF_WIDTH } from './world';
import { hashValue } from './scene';
import { crowdPose, crowdSlot, type MotionLevel } from './actors';
import { TANK_PARTS, TANK_SCALE } from './tank';
import { BARREL_DIMS } from './barrelDims';
import { effectiveFireInterval, type SimEvent, type SimState } from '../sim/sim';
import type { WeaponSpec } from '../sim/level';

/** Muzzle flashes are visible this long after the closed-form volley time (s). */
export const MUZZLE_WINDOW = 0.05;
const MUZZLE_MAX = 8;
const MUZZLE_Y = 1.3;
/** The squad's rifle tracer height over the deck (m) — the value every LEVEL has always used, now
 *  the DEFAULT of the `bossFx.tracerY` option below (L1/L2 stay bit-identical). */
const TRACER_Y = 1.05;

/**
 * Plan 19 D3: the three tank-pinned boss heights behind ONE option, so the L3 helicopter can raise
 * them to its own hull/pod heights (main.ts passes them for the heli level). Every default is the
 * literal this file used before, so constructing `Fx` without the option reproduces today's
 * behaviour bit-for-bit on L1/L2 (`fx.test.ts` and the heli.test.ts default-preservation pins).
 *  - `fireY`: the `bossFire` burst height (the tank's authored muzzle, the heli's rocket pod);
 *  - `hitY`: the `bossHit` spark height (the tank's turret flank, the heli's hull centre);
 *  - `tracerY`: the squad bullet TRACER height ceiling DURING the boss phase — against a hull hovering at
 *    2.6 m a flat 1.05 m stream reads as flying UNDER the boss while its bar drops (plan-gate r1
 *    opus P1), so the heli level raises the fire line toward the hull. Impl-gate r1 (opus P2 ×2):
 *    the height RAMPS from TRACER_Y at the squad to `tracerY` over TRACER_RISE_SPAN metres ahead
 *    (a flat ceiling spawned every tracer 1.55 m over the soldiers' heads); the run phase keeps
 *    the flat deck line on every level. Residual cosmetic note: bullets already in flight when
 *    the phase flips jump to their ramp height for their last ≤ ~1 s of flight — accepted (one
 *    frame, a dozen bullets, one level; the alternative is per-bullet spawn state, which breaks
 *    the pure-f discipline).
 */
export interface BossFx {
  fireY: number;
  hitY: number;
  tracerY: number;
  /** The ramp length for boss-phase tracers; default TRACER_RISE_SPAN (see BossFx doc). */
  tracerRiseSpan?: number;
}

/** Metres ahead of the squad over which a boss-phase tracer rises from TRACER_Y to tracerY.
 * Default 30; a heli level passes its own (standoff − 2 — the squad→hit-wall distance) so the
 * stream peaks EXACTLY at the hull instead of partway up it (impl-gate r2, opus/copilot). */
export const TRACER_RISE_SPAN = 30;

/** The tank's (== today's) boss FX heights — the `Fx` constructor's default. */
export const BOSS_FX_TANK: BossFx = {
  fireY: TANK_PARTS.muzzleY * TANK_SCALE,
  hitY: 2.4,
  tracerY: TRACER_Y,
};
/** Tracer colour at 2+ rate pips (plan 11): the RATE+ gate green, HDR. */
const TRACER_GREEN = [0.9, 2.0, 1.1];
/** Burst ring: events older than this (relative to the newest) can no longer be visible. Plan 14
 * P14.3: 1.3 → 1.75 so the delayed 1.6 s fuel-smoke layer of the barrel explosion lives out its full life
 * (debris shards live longer, in their OWN event list in debris.ts — this stays EVENTS-capped). */
export const RING_MAX_AGE = 1.75;
const RING_CAP = 64;
const DECK_Y = 0.04;
const G = 9.8;

/** One shared soft radial sprite (white, alpha falloff); null where there is no canvas (NullEngine). */
export function buildSpriteTexture(scene: Scene): DynamicTexture | null {
  if (typeof OffscreenCanvas === 'undefined') return null;
  const size = 64;
  const dyn = new DynamicTexture('fx-sprite', size, scene, true);
  const ctx = dyn.getContext() as CanvasRenderingContext2D | null;
  if (!ctx || typeof ctx.createRadialGradient !== 'function') {
    dyn.dispose();
    return null;
  }
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.25, 'rgba(255,255,255,0.8)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.2)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  dyn.update();
  dyn.hasAlpha = true;
  return dyn;
}

/** A burst kind: `add` picks the additive spark mesh, otherwise the alpha smoke mesh. */
export interface BurstSpec {
  add: boolean;
  n: number;
  life: number;
  speed: number;
  up: number;
  drag: number;
  grav: number;
  s0: number;
  s1: number;
  color: [number, number, number];
  /** Per-layer vertical offset from the event origin (metres): the explosion's ground-dust ring
   * rides well below its mid-barrel flash origin (plan 14 P14.3). Defaults to 0. */
  dy?: number;
  /** Grounded layers ignore the muzzle/event height. */
  groundY?: number;
  /** Delay before this layer appears, in seconds. */
  delay?: number;
}

const SPARK = { add: true, drag: 3, grav: G * 0.6 } as const;
const SMOKE = { add: false, drag: 2.2, grav: -0.6 } as const;
const CRATE_BREAK: BurstSpec[] = [
  { add: false, n: 10, life: 1, speed: 4.5, up: 5, drag: 1, grav: G, s0: 0.2, s1: 0.16, color: [0.45, 0.28, 0.12] },
  { ...SMOKE, n: 6, life: 0.8, speed: 2, up: 1.5, s0: 0.7, s1: 1.8, color: [0.5, 0.45, 0.38] },
];
/**
 * The plan-14 layered explosion shape (dossier §3: many simple layers beat one flipbook):
 * fireball flash, 26 faster orange sparks, fuel-dark 1.6 s smoke, flat ground dust ring. One
 * source for `explosion` AND the plan-15 `shellImpact` (the tank shell detonates with the same
 * shape — fx.test.ts pins the identity).
 */
const CONCRETE_BREAK: BurstSpec[] = [
  { ...SMOKE, grav: 0, n: 16, life: 1.1, speed: 4, up: 1.2, s0: 0.5, s1: 1.9, color: [0.48, 0.46, 0.42] },
  { ...SMOKE, grav: G, drag: 1, n: 8, life: 0.7, speed: 3, up: 3, s0: 0.18, s1: 0.1, color: [0.38, 0.37, 0.34] },
];
const EXPLOSION_LAYERS: BurstSpec[] = [
  { ...SPARK, drag: 4, n: 3, life: 0.28, speed: 1.4, up: 0.8, s0: 1.3, s1: 2.0, color: [1.55, 0.72, 0.2] },
  { ...SPARK, n: 26, life: 0.9, speed: 8, up: 3.5, s0: 0.35, s1: 0.08, color: [2.6, 1.0, 0.2] },
  { ...SMOKE, delay: 0.1, n: 12, life: 1.6, speed: 2.5, up: 2.2, s0: 0.8, s1: 2.6, color: [0.12, 0.1, 0.09] },
  { ...SMOKE, grav: 0, dy: -0.6, n: 16, life: 1.0, speed: 7, up: 0.5, drag: 2.6, s0: 0.6, s1: 2.0, color: [0.5, 0.44, 0.36] },
];

/** Exported for the reach test: the sparks must span the blast's x-interval (what you see is what hits). */
export const BURSTS: Partial<Record<SimEvent['type'], BurstSpec[]>> = {
  gateShot: [{ ...SPARK, n: 8, life: 0.35, speed: 4, up: 3, s0: 0.22, s1: 0.06, color: [0.9, 1.4, 2] }],
  crateHit: [{ ...SPARK, n: 5, life: 0.3, speed: 3.5, up: 3, s0: 0.2, s1: 0.05, color: [2, 1.4, 0.6] }],
  crateBreak: CRATE_BREAK,
  // Plan 09: no obstacleHit burst — a barricade under rifle fire takes ≈ 57 hits/s and would flush
  // the 64-entry ring; the shrinking HP bar is the hit read.
  obstacleBreak: CRATE_BREAK,
  // Plan 14 P14.3: the barrel explosion is a LAYERED burst — fireball flash (soft large additive
  // sprite, the existing soft radial), 26 faster orange sparks, fuel-dark 1.6 s smoke, and a flat
  // ground dust ring (the slamImpact precedent) offset down from the mid-barrel origin via `dy`.
  // Barricades select a concrete dust burst at draw time; barrels retain the crate-break fallback.
  explosion: EXPLOSION_LAYERS,
  // Plan 15 P15.3: the tank main gun. bossFire (at the muzzle, y = TANK_MUZZLE_Y from the
  // authored rig): lateral sparks + gun smoke + a ground dust ring under the muzzle. shellImpact
  // (at the band: the handleEvents zone special-case below, y 0.2) is the SAME layered shape as
  // the barrel explosion — one source, pinned identical by fx.test.ts.
  bossFire: [
    { ...SPARK, n: 14, life: 0.3, speed: 6, up: 2.5, s0: 0.4, s1: 0.08, color: [2.4, 1.3, 0.4] },
    { ...SMOKE, n: 8, life: 1.0, speed: 2.2, up: 1.6, s0: 0.6, s1: 1.8, color: [0.28, 0.26, 0.24] },
    { ...SMOKE, grav: 0, groundY: 0.15, n: 12, life: 0.8, speed: 5.5, up: 0.4, drag: 3, s0: 0.5, s1: 1.6, color: [0.5, 0.44, 0.36] },
  ],
  shellImpact: EXPLOSION_LAYERS,
  enemyKill: [
    { ...SMOKE, n: 6, life: 0.7, speed: 2.2, up: 1.5, s0: 0.5, s1: 1.4, color: [0.5, 0.44, 0.38] },
    { ...SPARK, n: 4, life: 0.4, speed: 3, up: 2.5, s0: 0.2, s1: 0.05, color: [2, 0.5, 0.35] },
  ],
  bossHit: [{ ...SPARK, n: 12, life: 0.4, speed: 5, up: 4, s0: 0.3, s1: 0.06, color: [2, 1.2, 0.5] }],
  // The dust ring: radial, mostly flat, slow.
  slamImpact: [{ ...SMOKE, grav: 0, n: 24, life: 1.2, speed: 9, up: 0.6, drag: 2.6, s0: 0.8, s1: 2.4, color: [0.5, 0.44, 0.36] }],
};

/** One live particle, written by `burstParticle`. */
export interface Particle {
  x: number;
  y: number;
  z: number;
  size: number;
  alpha: number;
}

/**
 * Closed-form particle i of a burst at (ox, oy, oz), `age` seconds after the stamped event. Ballistic
 * arc with exponential drag; y is floored just above the deck. False once dead (or not yet born).
 */
export function burstParticle(
  spec: BurstSpec,
  ox: number,
  oy: number,
  oz: number,
  seed: number,
  i: number,
  age: number,
  out: Particle,
): boolean {
  age -= spec.delay ?? 0;
  if (!(age >= 0 && age < spec.life)) return false;
  const ang = hashValue(i, seed, 1) * Math.PI * 2;
  const hs = spec.speed * (0.4 + 0.6 * hashValue(i, seed, 2));
  const vy = spec.up * (0.3 + 0.7 * hashValue(i, seed, 3));
  const d = (1 - Math.exp(-spec.drag * age)) / spec.drag;
  const u = age / spec.life;
  out.x = ox + Math.cos(ang) * hs * d;
  out.z = oz + Math.sin(ang) * hs * d;
  out.y = Math.max(DECK_Y, (spec.groundY ?? oy + (spec.dy ?? 0)) + vy * d - 0.5 * spec.grav * age * age);
  out.size = spec.s0 + (spec.s1 - spec.s0) * u;
  out.alpha = (1 - u) * (1 - u);
  return true;
}

/** Closed-form muzzle-flash age: time since the last volley, clamped to [0, ∞). */
export function volleyAge(interval: number, fireCooldown: number): number {
  return Math.max(0, interval - fireCooldown);
}

interface RingEntry {
  t: number;
  type: SimEvent['type'];
  x: number;
  y: number;
  /** NaN = boss-relative: resolved from the snapshot at draw time (squad is stationary there). */
  z: number;
  concrete: boolean;
}

/** Write a camera-facing / ground quad instance (uniform scale sx, sz) straight into a matrix buffer. */
function putQuad(buf: Float32Array, k: number, x: number, y: number, z: number, sx: number, sy: number, sz: number): void {
  const o = k * 16;
  buf.fill(0, o, o + 16);
  buf[o] = sx;
  buf[o + 5] = sy;
  buf[o + 10] = sz;
  buf[o + 12] = x;
  buf[o + 13] = y;
  buf[o + 14] = z;
  buf[o + 15] = 1;
}

/** One thin-instance quad draw: matrix + per-instance colour (rgb tint, a = fade). */
class QuadBatch {
  readonly mesh: Mesh;
  readonly matrices: Float32Array;
  readonly colors: Float32Array;
  count = 0;
  constructor(
    name: string,
    mesh: Mesh,
    material: StandardMaterial,
    readonly cap: number,
  ) {
    this.mesh = mesh;
    mesh.name = name;
    mesh.material = material;
    mesh.isPickable = false;
    mesh.alwaysSelectAsActiveMesh = true; // instances move every frame; skip bound refresh
    this.matrices = new Float32Array(cap * 16);
    this.colors = new Float32Array(cap * 4);
    mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    mesh.thinInstanceSetBuffer('color', this.colors, 4, false);
    this.begin();
  }
  begin(): void {
    this.count = 0;
  }
  color(k: number, r: number, g: number, b: number, a: number): void {
    this.colors[k * 4] = r;
    this.colors[k * 4 + 1] = g;
    this.colors[k * 4 + 2] = b;
    this.colors[k * 4 + 3] = a;
  }
  end(): void {
    this.mesh.thinInstanceCount = this.count;
    this.mesh.setEnabled(this.count > 0);
    if (this.count > 0) {
      this.mesh.thinInstanceBufferUpdated('matrix');
      this.mesh.thinInstanceBufferUpdated('color');
    }
  }
}

/** Four age-selected atlas cells, with one shared particle budget across their draws. */
class FlipbookBatch {
  readonly matrices: Float32Array;
  readonly colors: Float32Array;
  readonly frames: Uint8Array;
  private readonly batches: QuadBatch[];
  count = 0;
  constructor(scene: Scene, name: string, material: StandardMaterial, readonly cap: number) {
    this.matrices = new Float32Array(cap * 16);
    this.colors = new Float32Array(cap * 4);
    this.frames = new Uint8Array(cap);
    this.batches = Array.from({ length: 4 }, (_, frame) => {
      const mesh = MeshBuilder.CreatePlane(`${name}-${frame}`, { size: 1 }, scene);
      const uv = mesh.getVerticesData(VertexBuffer.UVKind)!;
      for (let i = 0; i < uv.length; i += 2) {
        uv[i] = (uv[i]! + frame % 2) / 2;
        uv[i + 1] = (uv[i + 1]! + Math.floor(frame / 2)) / 2;
      }
      mesh.setVerticesData(VertexBuffer.UVKind, uv);
      return new QuadBatch(frame === 0 ? name : `${name}-${frame}`, mesh, material, cap);
    });
  }
  begin(): void { this.count = 0; }
  color(index: number, r: number, g: number, b: number, a: number): void {
    const o = index * 4;
    this.colors[o] = r; this.colors[o + 1] = g; this.colors[o + 2] = b; this.colors[o + 3] = a;
  }
  end(): void {
    for (const batch of this.batches) batch.begin();
    for (let i = 0; i < this.count; i++) {
      const batch = this.batches[this.frames[i]!]!;
      for (let k = 0; k < 16; k++) batch.matrices[batch.count * 16 + k] = this.matrices[i * 16 + k]!;
      for (let k = 0; k < 4; k++) batch.colors[batch.count * 4 + k] = this.colors[i * 4 + k]!;
      batch.count++;
    }
    for (const batch of this.batches) batch.end();
  }
}

export class Fx {
  private readonly tracers: QuadBatch;
  private readonly muzzle: QuadBatch;
  private readonly sparks: QuadBatch;
  private readonly smoke: FlipbookBatch;
  private readonly fire: FlipbookBatch;
  private ring: RingEntry[] = [];
  /**
   * Plan 20 P20.3: drained `gatePass` sim times (the camera-impulse whoosh trigger). Stamped in
   * handleEvents, pruned against sim.t in update — a small events-capped list, same discipline as
   * the burst ring. The caller passes it back into cameraImpulse (lane D's wiring).
   */
  private gatePasses: number[] = [];
  private readonly scratch: Particle = { x: 0, y: 0, z: 0, size: 0, alpha: 0 };
  private readonly scratchPose = crowdPose();
  private readonly motion: MotionLevel;
  private readonly bossFx: BossFx;
  private bossFireOrigin: ((sim: SimState) => { x: number; y: number; z: number }) | null = null;
  private muzzleProvider: ((slot: number) => { x: number; y: number; z: number } | null) | null = null;

  constructor(
    scene: Scene,
    bulletPool: number,
    /** The tier's soldier cap: muzzles sit on the crowd as laid out at THIS cap. */
    private readonly maxSoldiers: number,
    private readonly fxParticles: number,
    private readonly weapons: WeaponSpec[],
    /** Plan 19 D3: boss-phase burst/tracer heights (LAST param, defaulted to the tank's — the
     *  shipped literals above — so every existing construction is bit-identical). */
    bossFx: BossFx = BOSS_FX_TANK,
  ) {
    this.motion = { weapons };
    this.bossFx = bossFx;
    const sprite = buildSpriteTexture(scene);
    const mat = (name: string, mode: number): StandardMaterial => {
      const m = new StandardMaterial(name, scene);
      m.disableLighting = true;
      m.emissiveColor = new Color3(1, 1, 1);
      m.diffuseTexture = sprite;
      m.alphaMode = mode;
      m.backFaceCulling = false;
      m.disableDepthWrite = true;
      m.fogEnabled = false;
      return m;
    };
    const add = mat('fx-add', Constants.ALPHA_ADD);
    const textured = (name: string, file: string, mode: number): StandardMaterial => {
      const material = mat(name, mode);
      if (typeof OffscreenCanvas !== 'undefined') {
        const texture = new Texture(`assets/${file}`, scene, false, false, Texture.TRILINEAR_SAMPLINGMODE);
        texture.hasAlpha = true;
        material.diffuseTexture = texture;
        material.useAlphaFromDiffuseTexture = true;
      }
      return material;
    };
    // Tracer: flat ribbon on the XZ plane (the chase cam looks along +z); the soft sprite stretched
    // along z reads as a hot-core streak.
    this.tracers = new QuadBatch('tracer', MeshBuilder.CreateGround('tracer', { width: 1, height: 1 }, scene), add, bulletPool);
    this.muzzle = new QuadBatch('muzzle', MeshBuilder.CreatePlane('muzzle', { size: 1 }, scene), textured('fx-flash', 'fx-muzzle.png', Constants.ALPHA_ADD), MUZZLE_MAX);
    this.sparks = new QuadBatch('fx-sparks', MeshBuilder.CreatePlane('fx-sparks', { size: 1 }, scene), add, fxParticles);
    this.smoke = new FlipbookBatch(scene, 'fx-smoke', textured('fx-smoke-material', 'fx-smoke.png', Constants.ALPHA_COMBINE), fxParticles);
    this.fire = new FlipbookBatch(scene, 'fx-fire', textured('fx-fire-material', 'fx-fire.png', Constants.ALPHA_ADD), fxParticles);
    this.reset();
  }

  /** Stamp this frame's drained sim events into the burst ring (they carry sim time `t`). */
  setBossFireOrigin(origin: (sim: SimState) => { x: number; y: number; z: number }): void {
    this.bossFireOrigin = origin;
  }

  handleEvents(events: SimEvent[], sim?: SimState): void {
    for (const ev of events) {
      // Plan 20 P20.3: the whoosh trigger rides the same drained-events pass (gatePass has no
      // burst, so without this stamp it would be dropped by the BURSTS filter below).
      if (ev.type === 'gatePass') {
        this.gatePasses.push(ev.t);
        this.gatePasses = this.gatePasses.slice(-GATE_PASS_CAP);
      }
      if (!BURSTS[ev.type]) continue;
      let x = 0;
      let y = 1.2;
      let z = NaN;
      if (ev.type === 'gateShot') x = ev.side === 'left' ? -0.5 : 0.5;
      if ('x' in ev && ev.x !== undefined) x = ev.x; // crates/obstacles/bossFire carry their own x (bossHit: the tank only, plan 15 fix round)
      if (ev.type === 'slamImpact' || ev.type === 'shellImpact') {
        // Plan 15 P15.3: the same zone special-case — origin = zone centre x, y 0.2, and the
        // event's own z (shellImpact carries the ABSOLUTE band z = tankBandZ; slamImpact stays
        // boss-relative like the brute's slam).
        x = (ev.zone[0] + ev.zone[1]) / 2;
        y = 0.2;
      }
      if (ev.type === 'bossHit') y = this.bossFx.hitY;
      if (ev.type === 'bossFire') y = this.bossFx.fireY; // the pod/muzzle height from the rig (default: the scaled tank muzzle)
      // Plan 14: the blast origin rides mid-barrel, flash core just above the lid (authored
      // height, so the stamp follows the barrel if the dims ever change).
      if (ev.type === 'explosion') y = BARREL_DIMS.height / 2 + 0.2;
      if ('z' in ev) z = ev.z;
      const origin = ev.type === 'bossFire' && sim && this.bossFireOrigin
        ? this.bossFireOrigin({ ...sim, t: ev.t, bossFireAt: ev.t, phase: 'boss',
          slam: { t: sim.slam?.t ?? 0, zone: [...ev.zone], active: 'inFlight' } }) : null;
      this.ring.push({ t: ev.t, type: ev.type, x: origin?.x ?? x * BRIDGE_HALF_WIDTH, y: origin?.y ?? y, z: origin?.z ?? z, concrete: ev.type === 'obstacleBreak' && ev.kind === 'barricade' });
      this.ring = this.ring.filter((r) => ev.t - r.t < RING_MAX_AGE).slice(-RING_CAP);
    }
  }

  /** Restart: drop the burst ring, every instance, and the whoosh list (plan 08 S0).
   * P20-5 fix: gatePasses too — a stamped gate-pass time survives a restart otherwise, and the
   * next cameraImpulse read would whoosh from a stale pre-restart event (phantom shake). */
  reset(): void {
    this.ring = [];
    this.gatePasses = [];
    for (const b of [this.tracers, this.muzzle, this.sparks, this.smoke, this.fire]) {
      b.begin();
      b.end();
    }
  }

  /** Rebuild every instance buffer from the snapshot + ring. Pure: same inputs, same matrices. */
  update(sim: SimState): void {
    // The whoosh lives exactly IMPULSE_LIFE like every camera impulse: prune against sim.t so the
    // list handed to cameraImpulse only ever holds live triggers (deterministic, no accumulation).
    this.gatePasses = this.gatePasses.filter((t) => sim.t - t < IMPULSE_LIFE);
    this.updateTracers(sim);
    this.updateMuzzle(sim);
    this.updateBursts(sim);
  }

  private updateTracers(sim: SimState): void {
    const b = this.tracers;
    b.begin();
    if (sim.phase === 'won' || sim.phase === 'lost') { b.end(); return; }
    const tier = Math.min(sim.weaponTier, this.weapons.length - 1);
    const w = this.weapons[tier];
    const heavy = w.splash !== undefined;
    const len = w.bulletSpeed / 30;
    const width = heavy ? 0.34 : 0.17;
    // Plan 11: the stream turns toward the RATE+ gate green per pip (half at 1, fully at 2; HDR so
    // bloom still catches it). Recomputed from state, so a strip reverts the whole stream in flight.
    const k = Math.min(1, sim.ratePips / 2);
    const [r, g, bl] = (heavy ? [2, 0.9, 0.3] : [2, 1.7, 1]).map((c, i) => c + (TRACER_GREEN[i]! - c) * k);
    // Plan 19 D3: during the boss fight the stream rises toward the boss's hull (see `BossFx`);
    // the run phase keeps today's flat deck line on every level, so L1/L2 are untouched. Impl-gate
    // r1 (opus P2): the rise is a RAMP from the squad — a near bullet stays low, only bullets past
    // TRACER_RISE_SPAN reach the ceiling (a flat ceiling spawned every tracer over the heads).
    const rise = sim.phase === 'boss' ? this.bossFx.tracerY - TRACER_Y : 0;
    const span = Math.max(1, this.bossFx.tracerRiseSpan ?? TRACER_RISE_SPAN);
    // Newest first (plan 10 B2): a full pool drops the distant tracers, not the ones at the muzzles.
    for (let i = sim.bullets.length - 1; i >= 0 && b.count < b.cap; i--) {
      const bullet = sim.bullets[i];
      if (!bullet.alive) continue;
      const u = Math.min(1, Math.max(0, (bullet.z - sim.squadZ) / span));
      putQuad(b.matrices, b.count, bullet.x * BRIDGE_HALF_WIDTH, TRACER_Y + rise * u, bullet.z - len * 0.4, width, 1, len);
      b.color(b.count, r, g, bl, 1);
      b.count++;
    }
    b.end();
  }

  setMuzzleProvider(provider: (slot: number) => { x: number; y: number; z: number } | null): void {
    this.muzzleProvider = provider;
  }

  private updateMuzzle(sim: SimState): void {
    const b = this.muzzle;
    b.begin();
    const w = this.weapons[Math.min(sim.weaponTier, this.weapons.length - 1)];
    const age = volleyAge(effectiveFireInterval(this.weapons, sim), sim.fireCooldown);
    if ((sim.phase === 'run' || sim.phase === 'boss') && age < MUZZLE_WINDOW && sim.n > 0) {
      const seed = Math.round((sim.t - age) * 1000);
      const shown = Math.min(sim.n, this.maxSoldiers);
      const fade = 1 - age / MUZZLE_WINDOW;
      // Resolve visible weapon sockets after squad animation and placement have updated.
      for (let i = 0; i < Math.min(shown, 9) && b.count < MUZZLE_MAX; i++) {
        if (hashValue(i, seed, 9) > 0.75) continue;
        const pose = crowdSlot(i, this.maxSoldiers, sim, this.motion, this.scratchPose);
        const s = (0.7 + 0.5 * hashValue(i, seed, 10)) * (w.splash !== undefined ? 1.8 : 1);
        const muzzle = this.muzzleProvider?.(i);
        putQuad(b.matrices, b.count, muzzle?.x ?? pose.x + 0.12, muzzle?.y ?? MUZZLE_Y, muzzle?.z ?? pose.z + 0.8, s * 0.55, s * 1.1, 1);
        b.color(b.count, 2, 1.5, 0.7, fade);
        b.count++;
      }
    }
    b.end();
  }

  private updateBursts(sim: SimState): void {
    this.sparks.begin();
    this.smoke.begin();
    this.fire.begin();
    const p = this.scratch;
    // Newest first, so a full pool drops the oldest bursts rather than the freshest.
    for (let e = this.ring.length - 1; e >= 0; e--) {
      const ev = this.ring[e];
      const specs = ev.concrete ? CONCRETE_BREAK : BURSTS[ev.type];
      if (!specs) continue;
      const oz = Number.isNaN(ev.z) ? sim.squadZ + 9 : ev.z;
      const age = sim.t - ev.t;
      for (let s = 0; s < specs.length; s++) {
        const spec = specs[s];
        const batch = spec.add ? (spec.s0 >= 1 ? this.fire : this.sparks) : this.smoke;
        const seed = (Math.round(ev.t * 1000) + s * 7919) | 0;
        for (let i = 0; i < spec.n && batch.count < batch.cap; i++) {
          if (spec.add && this.sparks.count + this.fire.count >= this.fxParticles) break;
          if (!burstParticle(spec, ev.x, ev.y, oz, seed, i, age, p)) continue;
          if (batch instanceof FlipbookBatch) batch.frames[batch.count] = Math.min(3, Math.floor((age - (spec.delay ?? 0)) / spec.life * 4));
          putQuad(batch.matrices, batch.count, p.x, p.y, p.z, p.size, p.size, 1);
          batch.color(batch.count, spec.color[0], spec.color[1], spec.color[2], p.alpha);
          batch.count++;
        }
      }
    }
    this.sparks.end();
    this.smoke.end();
    this.fire.end();
  }

  /** Probe/test: live instance counts per batch. */
  debugCounts(): { tracers: number; muzzle: number; sparks: number; smoke: number } {
    return { tracers: this.tracers.count, muzzle: this.muzzle.count, sparks: this.sparks.count + this.fire.count, smoke: this.smoke.count };
  }

  /** Test handle: a copy of the written matrices of one batch. */
  debugMatrices(kind: 'tracers' | 'muzzle' | 'sparks' | 'smoke'): number[] {
    const b = this[kind];
    const data = Array.from(b.matrices.subarray(0, b.count * 16));
    return kind === 'sparks' ? data.concat(Array.from(this.fire.matrices.subarray(0, this.fire.count * 16))) : data;
  }

  debugRing(): number {
    return this.ring.length;
  }

  /** Probe/test: the live gate-pass times (the cameraImpulse whoosh list lane D passes). */
  debugGatePasses(): number[] {
    return [...this.gatePasses];
  }
}

/** Camera shake sources: [amplitude m, frequency Hz]. Every one decays to zero by IMPULSE_LIFE. */
const IMPULSE_LIFE = 0.4;
const IMPULSE_MAX = 0.12;

/**
 * Plan 20 P20.3 camera juice (ALL tiers, 0 draws — plan-gate r3 opus P1-4): the two NEW sources
 * ride the same decaying-sinusoid shape and the same ≤ IMPULSE_MAX clamp as the existing ones.
 *  - boss fire: the sim's `bossFireAt` muzzle clock (sim.ts: the last bossFire time, −1 before the
 *    first fire — the bossHitAt pattern). A solid recoil thump, clearly above the crate/loss taps
 *    (0.05/0.06) but inside the small-impulse scale — since plan 27 the ENTRY kick and the slam
 *    tap (both at the full IMPULSE_MAX) outrank it.
 *  - gate pass: a small whoosh as the squad crosses a gate. Its trigger is the drained `gatePass`
 *    sim event (sim.ts:101/:634), which the sim does NOT mirror into the snapshot — so the TIMES
 *    are stamped into `Fx` (handleEvents, pruned against sim.t each update) and handed to
 *    `cameraImpulse` by the caller (lane D wires the list; the parameter is optional and every
 *    value is still f(sim-recorded times + snapshot), so the freeze rule holds).
 */
const BOSS_FIRE_AMP = 0.1;
const BOSS_FIRE_FREQ = 11;
const GATE_PASS_AMP = 0.03;
const GATE_PASS_FREQ = 7;
/**
 * Plan 27 Phase 1 (boss reveal): the entry kick — ONE decaying jolt on the standoff-arrival
 * frame, keyed off `bossEnterAt` (the bossHitAt pattern: < 0 ⇒ rest pose) and ZERO unless
 * `bossRevealAt ≥ 0`: the kick punctuates the reveal beat, so a skipped reveal (enemy threat
 * at the threshold) gets no kick at all — no beat, no punctuation (r2 opus N-7). It TIES the slam
 * source as the heaviest tap in the budget (the slam's `ring1` also runs at the full IMPULSE_MAX, so
 * neither tap outranks the other; x at the full IMPULSE_MAX, y at 0.7×): the release of the 1 s
 * silence is the moment the camera should flinch hardest. Own amp/freq constants, ring1's shared
 * decay; the post-sum IMPULSE_MAX clamp keeps the total ≤ 0.12 m per axis, and reduced motion zeroes
 * the whole impulse via `scale` (main.ts) — no new setting.
 */
const BOSS_ENTER_AMP = IMPULSE_MAX;
const BOSS_ENTER_FREQ_X = 9;
const BOSS_ENTER_FREQ_Y = 12;
/** Hard cap on the stamped gate-pass times (IMPULSE_LIFE pruning keeps it tiny anyway). */
const GATE_PASS_CAP = 16;

function ring1(age: number, amp: number, freq: number, seed: number): number {
  if (!(age >= 0 && age < IMPULSE_LIFE)) return 0;
  const k = 1 - age / IMPULSE_LIFE;
  const phase = hashValue(Math.round(seed * 1000), 3, 17) * Math.PI * 2;
  return amp * k * k * Math.sin(2 * Math.PI * freq * age + phase);
}

/**
 * Plan 08 G2 camera impulse: a pure offset (metres) from sim-recorded times only — slam (sim.slam.t
 * while silent is the time since the impact; also rumbles once as the boss arrives), crate break
 * (brokenAt), squad losses (newest sim.losses entry), and plan 20 P20.3: boss fire (sim.bossFireAt,
 * the heavier recoil) + the gate-pass whoosh (the drained `gatePass` times handed in as
 * `gatePasses` — see the constants above; lane D wires the list, the parameter is optional and
 * every value is still f(sim-recorded times + snapshot), so the freeze rule holds), and plan 27:
 * the boss-entry kick (sim.bossEnterAt, reveal-only — see BOSS_ENTER_AMP). Decaying
 * sinusoid, hashed phase, each component clamped to ±0.12 m; `scale` is 0 under reduced motion.
 */
export function cameraImpulse(
  sim: SimState,
  scale: number,
  gatePasses: readonly number[] = [],
): { x: number; y: number } {
  if (scale <= 0) return { x: 0, y: 0 };
  let x = 0;
  let y = 0;
  if (sim.slamAt !== null) {
    x += ring1(sim.t - sim.slamAt, IMPULSE_MAX, 13, sim.slamAt);
    y += ring1(sim.t - sim.slamAt, IMPULSE_MAX * 0.7, 17, sim.slamAt + 1);
  }
  for (const c of sim.crates) {
    if (c.brokenAt === null) continue;
    x += ring1(sim.t - c.brokenAt, 0.05, 15, c.brokenAt);
    y += ring1(sim.t - c.brokenAt, 0.05, 19, c.brokenAt + 1);
  }
  const loss = sim.losses[sim.losses.length - 1];
  if (loss) {
    x += ring1(sim.t - loss.t, 0.06, 16, loss.t);
    y += ring1(sim.t - loss.t, 0.04, 21, loss.t + 1);
  }
  // Plan 20 P20.3: boss-fire recoil (bossFireAt < 0 ⇒ no fire yet — the bossHitAt guard pattern).
  if (sim.bossFireAt >= 0) {
    x += ring1(sim.t - sim.bossFireAt, BOSS_FIRE_AMP, BOSS_FIRE_FREQ, sim.bossFireAt);
    y += ring1(sim.t - sim.bossFireAt, BOSS_FIRE_AMP * 0.7, 14, sim.bossFireAt + 1);
  }
  // Plan 27 Phase 1: the boss-entry kick — reveal-only (bossRevealAt −1 = skipped reveal ⇒ no
  // beat to punctuate) and rest-pose until arrival (bossEnterAt < 0), both the guard pattern.
  if (sim.bossEnterAt >= 0 && sim.bossRevealAt >= 0) {
    x += ring1(sim.t - sim.bossEnterAt, BOSS_ENTER_AMP, BOSS_ENTER_FREQ_X, sim.bossEnterAt);
    y += ring1(sim.t - sim.bossEnterAt, BOSS_ENTER_AMP * 0.7, BOSS_ENTER_FREQ_Y, sim.bossEnterAt + 1);
  }
  for (const g of gatePasses) {
    x += ring1(sim.t - g, GATE_PASS_AMP, GATE_PASS_FREQ, g);
    y += ring1(sim.t - g, GATE_PASS_AMP * 0.6, 9, g + 1);
  }
  const c = (v: number): number => Math.max(-IMPULSE_MAX, Math.min(IMPULSE_MAX, v * scale));
  return { x: c(x), y: c(y) };
}
